import { z } from "zod";
import { getDependenciesContract } from "@project/shared/tool-contracts";
import { defineTool } from "../harness/index.js";
import { defineContractTool } from "./contract-tool.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { textResult } from "./helpers.js";

export function buildGetDependenciesTool(connection: AgentConnection) {
  return defineContractTool(
    getDependenciesContract,
    async () => {
      try {
        const deps = await connection.call("getDependencies", {
          sessionId: connection.sessionId,
        });
        return textResult(JSON.stringify(deps, null, 2));
      } catch (error) {
        return textResult(
          `Failed to get dependencies: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

export function buildGetSuggestionsTool(connection: AgentConnection) {
  return defineTool(
    "get_suggestions",
    "List project suggestions sorted by vote score. Filter by status or cap with limit (default 20). Suggestions are project-level ideas, not tasks — use get_task for tasks.",
    {
      status: z
        .string()
        .optional()
        .describe(
          "Filter by status: Planning, Open, InProgress, ReviewPR, ReviewDev, ReviewLive, Complete, Cancelled",
        ),
      limit: z.number().int().min(1).max(100).optional().describe("Max results (default 20)"),
    },
    async ({ status, limit }) => {
      try {
        const suggestions = await connection.call("getSuggestions", {
          sessionId: connection.sessionId,
          status,
          limit,
        });
        if (suggestions.length === 0) {
          return textResult("No suggestions found.");
        }
        return textResult(JSON.stringify(suggestions, null, 2));
      } catch (error) {
        return textResult(
          `Failed to get suggestions: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}
